import React, { useEffect, useState } from "react";
import { Gauge, UserPlus, X, AlertTriangle, LayoutGrid, CalendarRange } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import WeeklyCapacityCalendar from "@/components/WeeklyCapacityCalendar";

const fmtDateDe = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : iso || "");

function utilColor(pct) {
  if (pct >= 100) return { bar: "#EF4444", chip: "bg-red-50 text-red-700 border-red-200", label: "Voll ausgelastet" };
  if (pct >= 70) return { bar: "#F59E0B", chip: "bg-amber-50 text-amber-800 border-amber-200", label: "Fast ausgelastet" };
  return { bar: "#10B981", chip: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Kapazität frei" };
}

function AssignClientDialog({ coach, onClose, onAssigned }) {
  const [waiting, setWaiting] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get("/clients/waiting", { params: { exclude_coach_id: coach.user_id } }).then((r) => setWaiting(r.data || [])).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setWaiting([]);
    });
  }, [coach.user_id]);

  const assign = async (client) => {
    const hasOtherCoach = (client.current_coach_names || []).length > 0;
    if (hasOtherCoach && !window.confirm(
      `"${client.name}" wird aktuell von ${client.current_coach_names.join(", ")} betreut. ` +
      `Jetzt an ${coach.name} übergeben? Der bisherige Coach wird dadurch entlastet.`
    )) return;
    setBusyId(client.client_id);
    try {
      await api.put(`/clients/${client.client_id}/assign-coach`, { coach_user_id: coach.user_id });
      toast.success(`${client.name} wurde ${coach.name} zugewiesen`);
      onAssigned();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="assign-client-overlay">
      <div className="bg-white border border-slate-200 max-w-md w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="assign-client-dialog">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-medium text-slate-900">Kunde zu {coach.name} zuweisen</div>
          <button onClick={onClose} data-testid="assign-client-close"><X size={16} className="text-slate-400 hover:text-slate-800" /></button>
        </div>
        <div className="p-2">
          {waiting === null && <div className="text-xs text-slate-400 italic p-4">Lädt…</div>}
          {waiting && waiting.length === 0 && (
            <div className="text-xs text-slate-400 italic p-4">Keine anderen aktiven Kunden verfügbar</div>
          )}
          {(waiting || []).map((c) => (
            <div key={c.client_id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50" data-testid={`waiting-client-row-${c.client_id}`}>
              <div>
                <div className="text-sm text-slate-900">{c.name}</div>
                <div className="text-[11px] text-slate-500 font-mono">
                  {c.total_ue} UE
                  {(c.current_coach_names || []).length > 0 && (
                    <span className="text-amber-600"> · aktuell bei {c.current_coach_names.join(", ")}</span>
                  )}
                  {(c.current_coach_names || []).length === 0 && <span className="text-emerald-600"> · ohne Coach</span>}
                </div>
              </div>
              <button onClick={() => assign(c)} disabled={busyId === c.client_id} data-testid={`waiting-client-assign-${c.client_id}`}
                className="text-xs font-medium text-white bg-slate-900 hover:bg-black px-3 py-1.5 disabled:opacity-50">
                {busyId === c.client_id ? "…" : (c.current_coach_names || []).length > 0 ? "Übernehmen" : "Zuweisen"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CoachCapacityCard({ c, canAssign, onAssignClick }) {
  const meta = utilColor(c.utilization_pct);
  const overbooked = c.utilization_pct >= 100;
  return (
    <div className={`border border-slate-200 bg-white p-4 ${overbooked ? "capacity-overbooked" : ""}`}
      data-testid={`capacity-card-${c.user_id}`} data-overbooked={overbooked}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-sm font-medium text-slate-900">{c.name}</div>
        <span className={`shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 border ${meta.chip}`}>
          {overbooked && <AlertTriangle size={10} strokeWidth={2} />}
          {overbooked ? "Überbucht" : meta.label}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-heading text-2xl font-light tracking-tighter text-slate-900" data-testid={`capacity-pct-${c.user_id}`}>
          {c.utilization_pct}%
        </span>
        <span className="text-xs text-slate-500">ausgelastet · {c.committed_hours_now}h / {c.weekly_capacity_hours}h pro Woche</span>
      </div>
      <div className="h-1.5 bg-slate-100 mt-2">
        <div className="h-full transition-[width] duration-500" style={{ width: `${Math.min(100, c.utilization_pct)}%`, background: meta.bar }} />
      </div>
      <div className="text-[11px] text-slate-500 mt-2" data-testid={`capacity-free-now-${c.user_id}`}>
        {c.free_slots_now > 0
          ? <>Jetzt frei für <b className="text-slate-900">{c.free_slots_now}</b> neue Kunden ({c.free_hours_now}h übrig)</>
          : "Aktuell keine freie Kapazität"}
      </div>
      {c.timeline.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2 space-y-1">
          <div className="text-[9px] font-bold uppercase tracking-overline text-slate-400">Wird frei</div>
          {c.timeline.map((t, i) => (
            <div key={i} className="text-[11px] text-slate-600 flex items-center justify-between" data-testid={`capacity-timeline-${c.user_id}-${i}`}>
              <span>
                ab {fmtDateDe(t.date)} · nach „{t.client_name}"
                {t.vacation_weeks_included > 0 && (
                  <span className="text-amber-600"> (inkl. {t.vacation_weeks_included} Wo. Urlaub)</span>
                )}
              </span>
              <span className="font-mono text-slate-900">{t.cumulative_free_slots} frei</span>
            </div>
          ))}
        </div>
      )}
      {canAssign && (
        <button onClick={onAssignClick} data-testid={`capacity-assign-button-${c.user_id}`}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-medium text-[#00807E] border border-[#00807E]/30 hover:bg-[#00807E]/5 px-3 py-1.5">
          <UserPlus size={13} /> Kunde zuweisen
        </button>
      )}
    </div>
  );
}

export default function CoachCapacityPanel({ mine = false, compact = false }) {
  const { user } = useAuth();
  const canAssign = !mine && (user?.role === "admin" || user?.role === "manager" || !!user?.is_master_admin);
  const [data, setData] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [view, setView] = useState("calendar");

  const load = () => {
    api.get(mine ? "/reports/my-capacity" : "/reports/coach-capacity")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-xs text-slate-400 italic py-4">Lädt…</div>;

  if (compact) {
    if (!data.coaches?.length) return null;
    return (
      <div data-testid="dashboard-my-capacity" className="mb-10">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Gauge size={13} /> Meine Kapazität · nächste 6 Wochen</div>
          <a href="/meine-kapazitaet" className="text-xs text-slate-500 underline underline-offset-4 hover:text-slate-900" data-testid="dashboard-my-capacity-link">Details</a>
        </div>
        <WeeklyCapacityCalendar coaches={data.coaches} canAssign={false} onAssignClick={() => {}} />
      </div>
    );
  }

  return (
    <div data-testid="coach-capacity-panel" className="mt-8">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Gauge size={14} strokeWidth={1.5} className="text-slate-500" />
          <p className="text-xs text-slate-500 max-w-2xl">
            Kapazitäts-Vorschau je Coach — Annahme: jeder aktive Kunde bindet 3 Termine à 2 Std./Woche
            ({data.hours_per_client_per_week}h bzw. {data.ue_per_client_per_week} UE), bis seine Rest-UE aufgebraucht sind
            (pro Kunde in der Kunden-Akte überschreibbar). Kunden mit Abschnitten laufen phasenweise:
            Onboarding → Abschnitt 1 → Abschnitt 2 → … → Offboarding, automatisch nach UE-Anteil auf den
            Kunden-Zeitraum verteilt — sobald die eigene Phase endet, ist die zuständige Person für diesen
            Kunden wieder frei, auch wenn der Vertrag insgesamt noch läuft. Kapazität pro Person = eigenes
            Wochenstunden-Ziel. Genehmigter Urlaub/Abwesenheit reduziert die Wochen-Kapazität anteilig (orange schraffiert).
          </p>
        </div>
        <div className="flex items-center gap-1 border border-slate-200 shrink-0" data-testid="capacity-view-toggle">
          <button onClick={() => setView("calendar")} data-testid="capacity-view-calendar-button"
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 ${view === "calendar" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <CalendarRange size={13} /> Wochen-Kalender
          </button>
          <button onClick={() => setView("cards")} data-testid="capacity-view-cards-button"
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 ${view === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <LayoutGrid size={13} /> Kompakt-Karten
          </button>
        </div>
      </div>
      {view === "calendar" ? (
        <WeeklyCapacityCalendar coaches={data.coaches} canAssign={canAssign} onAssignClick={setAssignTarget} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.coaches.map((c) => (
            <CoachCapacityCard key={c.user_id} c={c} canAssign={canAssign} onAssignClick={() => setAssignTarget(c)} />
          ))}
        </div>
      )}
      {assignTarget && (
        <AssignClientDialog coach={assignTarget} onClose={() => setAssignTarget(null)} onAssigned={load} />
      )}
    </div>
  );
}
